import { AuthUtils } from '@portal/portal-core';
import { get } from 'lodash-es';
import React from 'react';
import styled from 'styled-components';

import { FjCheckbox, fjModalSizes } from 'components/primitives';
import { Button } from 'components/styled-primitives';
import { SectionBox, SectionBoxFooter } from 'components/licenses/section-box';
import type { RootState } from 'reducers';
import { filterInvitestoPendingOnly } from 'selectors';
import * as Utils from 'utils';
import { AlertMessage, DatePicker, Modal } from '../../../../styled-primitives';
import { currentUserHasSudoClaim } from 'ducks/auth-client/utils';
// The named (presentational) export, not the barrel's connected default: that one injects its
// own catalog from redux, whereas here the list comes from the license.
import { ProductFeaturesManager } from 'components/super-admin/modals/license-modal/components/product-features-manager';
import {
  isLicenseEnabled,
  selectionState,
  selectionSummary,
  nextStateOnToggle,
  type SeatFeatureRow,
  type LicenseFeature,
} from 'components/licenses/seat-feature-state';
import ResponseModal from './response-modal';

const { isUserSeatActive } = AuthUtils;

const REMOVE = 'remove';
const ADD = 'add';
const ARCHIVE = 'archive';
const NOT_USER_ADMIN_ERROR_MESSAGE = 'You must be an admin of this cost center to manage users.';

interface UserSeat {
  userId: string;
  startDate: string | null;
  endDate: string | null;
  suspended: boolean;
}

interface ProductAccess {
  id: string;
  name: string;
  /** Product-access level feature values, keyed by ProductAccessFeature id. */
  features?: Record<string, LicenseFeature>;
  users: Record<string, UserSeat>;
}

interface ProductSetUsersEndDateArgs {
  productName: string;
  selectedIdsForDateMod: string[];
  costCenterId: unknown;
  productAccessId: string;
  // Runtime passes a Date/null here; index.tsx types the dispatcher arg as
  // string, so this field is a cross-file boundary — `unknown` accepts both.
  endDate: unknown;
}

interface ProductSetUsersArchivedDateArgs {
  productName: string;
  userIds: string[];
  costCenterId: unknown;
  productAccessId: string;
  // Runtime passes a Date here; index.tsx types the dispatcher arg as string.
  archivedDate: unknown;
}

interface ManageProductUsersArgs {
  productName: string;
  rowsSelectedIdsArray: string[];
  costCenterId: unknown;
  productAccessId: string;
  suspended: boolean;
}

interface Props {
  cloudAdminState?: unknown;
  userAdminState: RootState['userAdmin'];
  adminInvites: unknown;
  selectedRows: string[];
  selectedRowsAll: unknown;
  currentProductAccess: ProductAccess;
  selectedProduct: unknown;
  selectedRegion?: unknown;
  hideModal: () => void;
  clearRowsSelected: () => void;
  closeProductManageUsersResults: () => void;
  closeSetUsersSuspendedResults: () => void;
  closeProductSetUsersArchivedDateResults: () => void;
  // Method syntax (bivariant params) so index.tsx's string-typed dispatcher
  // args stay assignable to these unknown-typed args.
  productSetUsersEndDate(args: ProductSetUsersEndDateArgs): void;
  productSetUsersArchivedDate(args: ProductSetUsersArchivedDateArgs): void;
  manageProductUsers(args: ManageProductUsersArgs): void;
  // Per-seat feature overrides. Only meaningful for a superadmin; the section that uses these
  // does not render otherwise.
  seatFeatures?: SeatFeatureRow[];
  fetchSeatFeatures?: (costCenterId: string, productAccessId: string) => void;
  setSeatFeature?: (args: {
    costCenterId: string;
    productAccessId: string;
    userIds: string[];
    featureName: string;
    value: boolean | string | number;
    enabled: boolean | null;
  }) => void;
}

interface State {
  errorMessageArray: string[];
  selectedOption: string | null;
  seatDeactivationDate: Date | null | undefined;
  defaultDateString: string;
  dateModificationRequired: boolean;
  showArchiveConfirmation: boolean;
  // Staged per-feature changes, applied on Apply alongside the seat-status changes.
  // Absent key = untouched; true/false = set for every selected seat.
  seatFeaturePending: Record<string, boolean>;
  // What the feature Apply just did, for the confirmation that replaces this modal. Null
  // until it is clicked. Captured at Apply time because it describes the state the seats were
  // in beforehand, which the store no longer reports once the writes land.
  seatFeatureConfirmation: SeatFeatureOutcome[] | null;
}

/** One feature's before-and-after, as the confirmation reports it. */
interface SeatFeatureOutcome {
  featureName: string;
  enabled: boolean;
  total: number;
  wasEnabled: number;
  wasDisabled: number;
  wasMixed: boolean;
}

const RadioButton = styled(FjCheckbox).attrs({
  type: 'radio',
})`
  margin-top: 10px;
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.defaultGreyBorderColor};
  }
`;

// The section heading already provides the separation here, so drop the container's own top
// margin. Scoped to this consumer: the license modal has no heading and still needs it.
const FeatureSection = styled.div`
  & > div + div {
    margin-top: 0;
  }
`;

// Red because the toggle understates a mixed selection: it reads off whenever the seats
// disagree, even when most of them are enabled.
const MixedNote = styled.div`
  color: ${({ theme }) => theme.colors.error};
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  margin-bottom: 12px;
`;

// The modal-level row. Unlike ActionRow it is not pinned inside a bordered box, so it carries
// its own separation from the last section instead of bleeding to a border.
const ModalActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const StatusContainer = styled.div`
  margin-top: ${({ theme }) => theme.grid.units(1)};
  margin-bottom: ${({ theme }) => theme.grid.units(0.5)};

  & > div {
    margin-left: 5px;
  }
`;

class ManageUsersModal extends React.Component<Props, State> {
  locRowsSelected: unknown[];

  constructor(props: Props) {
    super(props);
    const allUsers =
      (get(props, 'currentProductAccess.users') as Record<string, UserSeat> | undefined) || {};
    const users = props.selectedRows.reduce<UserSeat[]>(
      (last, rowId) => (allUsers[rowId] ? [...last, allUsers[rowId]] : last),
      [],
    );
    // If all selected user seats have same status, set initial state
    const initialState =
      (users.every((user) => !Utils.seatActive(user.startDate, user.endDate, user.suspended)) &&
        REMOVE) ||
        (users.every((user) => Utils.seatActive(user.startDate, user.endDate, user.suspended)) &&
          ADD) ||
          null;
    let initialEndDate: Date | null | undefined;
    const firstEndDate = users[0].endDate;
    if (users.every((user) => user.endDate === firstEndDate)) {
      if (firstEndDate !== null) {
        initialEndDate = new Date(firstEndDate);
      }
    } else {
      initialEndDate = null;
    }
    this.state = {
      errorMessageArray: [],
      selectedOption: initialState,
      seatDeactivationDate: initialEndDate,
      defaultDateString: 'Pick an end date',
      dateModificationRequired: false,
      showArchiveConfirmation: false,
      seatFeaturePending: {},
      seatFeatureConfirmation: null,
    };
    this.locRowsSelected = [];
  }

  componentDidMount(): void {
    // Check admin status when modal opens and display error if not admin
    if (!this.isCurrentUserAdmin()) {
      this.setState({
        errorMessageArray: [NOT_USER_ADMIN_ERROR_MESSAGE],
      });
    }
    // Only a superadmin can read seat overrides, and the endpoint 401s for anyone else, so
    // don't even issue the request unless the sudo claim is present.
    const { currentProductAccess, userAdminState, fetchSeatFeatures } = this.props;
    if (currentUserHasSudoClaim() && fetchSeatFeatures && currentProductAccess?.id) {
      const costCenterId = userAdminState?.costCenter?.id;
      if (costCenterId) {
        fetchSeatFeatures(costCenterId, currentProductAccess.id);
      }
    }

    // Add keyboard event listener for ESC key
    document.addEventListener('keydown', this.handleKeyPress);
  }

  componentWillUnmount(): void {
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleKeyPress);
  }

  isCurrentUserAdmin(): boolean {
    const { userAdminState } = this.props;
    const { costCenter } = userAdminState;

    if (!costCenter) {
      return false;
    }

    // Get current user ID from JWT token
    const authToken =
      localStorage.getItem('portal.authToken') || sessionStorage.getItem('portal.authToken');
    if (!authToken) {
      return false;
    }

    try {
      const userId = AuthUtils.getUserIdFromJWT(authToken);
      return AuthUtils.isUserAdmin(userId, costCenter);
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  checkAdminAndSetError(stateUpdates: Partial<State> = {}): boolean {
    if (!this.isCurrentUserAdmin()) {
      // Spreading a `Partial<State>` makes each key optional, which React's
      // `setState<K>` (expects `Pick<State, K>` with required keys) rejects.
      // The runtime object is a valid partial state update; assert the shape.
      this.setState({
        ...stateUpdates,
        errorMessageArray: [NOT_USER_ADMIN_ERROR_MESSAGE],
      } as State);
      return false;
    }
    return true;
  }

  selectedUsersActiveIds(): string[] {
    const { selectedRows, currentProductAccess } = this.props;
    const userSeats = currentProductAccess.users;
    const rowsSelectedIdsArray = [...selectedRows];
    const filteredUserIds: string[] = [];
    rowsSelectedIdsArray.forEach((id) => {
      const currentUser = userSeats[id];
      const { suspended, startDate, endDate } = currentUser;
      // if we ever allow startDates to be set in the future, we're going to need a different utility
      // than isUserSeatActive, as future startDate seats have to be substracted from open seat count
      if (currentUser && AuthUtils.isUserSeatActive(suspended, startDate, endDate)) {
        filteredUserIds.push(currentUser.userId);
      }
    });
    return filteredUserIds;
  }

  isActivating(): boolean {
    const change = this.state.selectedOption;
    if (change === ADD) {
      return true;
    }
    return false;
  }

  getSuspendedChanges(
    rowsSelectedIdsArray: string[],
    productUsers: Record<string, UserSeat>,
  ): string[] {
    // suspended:false will likely be depreciated
    // only apply suspended:false to these ids if being reactivated
    // filter out any user ids that already have the desired value
    const suspendedChangesIds: string[] = [];
    rowsSelectedIdsArray.forEach((id) => {
      const currentUser = productUsers[id];
      if (this.isActivating() && currentUser && currentUser.suspended !== false) {
        suspendedChangesIds.push(currentUser.userId);
      }
    });
    return suspendedChangesIds;
  }

  getEndDateChanges(
    rowsSelectedIdsArray: string[],
    productUsers: Record<string, UserSeat>,
  ): string[] {
    // filter out any user ids that already have the new value
    const newEndDate = this.state.seatDeactivationDate;
    const endDateChangesIds: string[] = [];
    rowsSelectedIdsArray.forEach((id) => {
      const currentUser = productUsers[id];
      const { suspended, startDate, endDate } = currentUser;
      // First, deal with the activation usecase.
      // Don't try to add if the endDate already null, or the proposed new deactivation date.
      if (this.isActivating() && currentUser && endDate !== newEndDate) {
        endDateChangesIds.push(currentUser.userId);
      } else if (
        !this.isActivating() &&
        currentUser &&
        isUserSeatActive(suspended, startDate, endDate)
      ) {
        // Deactivation requires a different approach.
        // Don't try to overwrite their existing endDate if seat is already expired.
        endDateChangesIds.push(currentUser.userId);
      }
    });
    return endDateChangesIds;
  }

  enableApply(): boolean {
    // Check if current user is an admin
    if (!this.isCurrentUserAdmin()) {
      return false;
    }

    const { currentProductAccess: productAccess, selectedRows } = this.props;
    if (productAccess) {
      const rowsSelectedIdsArray = [...selectedRows];
      if (
        this.getSuspendedChanges(rowsSelectedIdsArray, productAccess.users).length > 0 ||
        this.getEndDateChanges(rowsSelectedIdsArray, productAccess.users).length > 0 ||
        this.state.selectedOption === ARCHIVE
      )
        return true;
    }
    return false;
  }

  updateSeatsEndDate(): void {
    const change = this.state.selectedOption;
    const {
      userAdminState,
      currentProductAccess: productAccess,
      selectedRows,
      productSetUsersEndDate,
    } = this.props;
    const costCenterId = userAdminState.costCenter!.id;
    const rowsSelectedIdsArray = [...selectedRows];
    const endDateChangesIds = this.getEndDateChanges(rowsSelectedIdsArray, productAccess.users);
    if (
      productAccess &&
      endDateChangesIds.length > 0 &&
      change === ADD &&
      this.state.dateModificationRequired
    ) {
      // reactivate seats
      productSetUsersEndDate({
        productName: productAccess.name,
        selectedIdsForDateMod: endDateChangesIds,
        costCenterId,
        productAccessId: productAccess.id,
        endDate: this.state.seatDeactivationDate,
      });
    } else if (productAccess && endDateChangesIds.length > 0 && change === REMOVE) {
      // set the deactivation date to today for all selected seats
      productSetUsersEndDate({
        productName: productAccess.name,
        selectedIdsForDateMod: endDateChangesIds,
        costCenterId,
        productAccessId: productAccess.id,
        endDate: new Date(),
      });
    }
  }

  updateSeatsArchivedDate(): void {
    const {
      userAdminState,
      currentProductAccess: productAccess,
      selectedRows,
      productSetUsersArchivedDate,
    } = this.props;
    const costCenterId = userAdminState.costCenter!.id;
    const rowsSelectedIdsArray = [...selectedRows];
    const userIds = rowsSelectedIdsArray.map((id) => productAccess.users[id].userId);
    if (productAccess && userIds.length > 0) {
      productSetUsersArchivedDate({
        productName: productAccess.name,
        userIds,
        costCenterId,
        productAccessId: productAccess.id,
        archivedDate: new Date(),
      });
    }
  }

  updateSeatsSuspended(): void {
    const {
      userAdminState,
      currentProductAccess: productAccess,
      selectedRows,
      manageProductUsers,
    } = this.props;
    const costCenterId = userAdminState.costCenter!.id;
    const rowsSelectedIdsArray = [...selectedRows];
    const suspendedChanges = this.getSuspendedChanges(rowsSelectedIdsArray, productAccess.users);
    if (productAccess && suspendedChanges.length > 0) {
      // unsuspend the flag for all seats as this flag will be deprecated soon
      manageProductUsers({
        productName: productAccess.name,
        rowsSelectedIdsArray: suspendedChanges,
        costCenterId,
        productAccessId: productAccess.id,
        suspended: false,
      });
    }
  }

  updateSeats(): void {
    const change = this.state.selectedOption;
    if (change === ADD) {
      // Dates need to be applied AFTER the user seat's supended flag is set to false.
      this.updateSeatsSuspended();
      this.updateSeatsEndDate();
    } else if (change === REMOVE) {
      // Deactivation dates need to be applied before user seat's supended flag is set to true.
      // IN OTHER WORDS, REVERSE THE ORDER OF OPERATION
      this.updateSeatsEndDate();
      this.updateSeatsSuspended();
    } else if (change === ARCHIVE) {
      // Show confirmation modal before archiving
      this.setState({ showArchiveConfirmation: true });
    }
  }

  handleChange(): void {
    const { adminInvites, currentProductAccess: productAccess, selectedRows } = this.props;
    const locErrorMessageArray: string[] = [];

    // Check if current user is an admin
    if (!this.isCurrentUserAdmin()) {
      locErrorMessageArray.push(NOT_USER_ADMIN_ERROR_MESSAGE);
      this.setState({ errorMessageArray: locErrorMessageArray });
      return;
    }

    // get seat usage object if this is FlowJo or SeqGeq
    let seatUsageObject: ReturnType<typeof AuthUtils.getSeatUsageForProductAccess> | undefined;
    if (productAccess) {
      // `adminInvites` arrives as `unknown` from the untyped selector boundary.
      const pendingInvites = filterInvitestoPendingOnly(adminInvites as unknown[], productAccess);
      seatUsageObject = AuthUtils.getSeatUsageForProductAccess(
        productAccess,
        pendingInvites.length,
      );
    }
    // if adding users, check that there is are available seats
    if (seatUsageObject && this.state.selectedOption === ADD) {
      const { openSeats } = seatUsageObject;
      // selected users who are currently already active seats
      const activeCount = this.selectedUsersActiveIds().length;
      const rowsSelectedIdsArray = [...selectedRows];
      const inactiveSelectedSeatCount = rowsSelectedIdsArray.length - activeCount;
      const remainingSeats = openSeats - inactiveSelectedSeatCount;
      const seatsPluralized = Utils.pluralize(openSeats, 'seat');
      if (openSeats > 0 && remainingSeats < 0) {
        locErrorMessageArray.push(
          `Your license currently has ${openSeats} open ${seatsPluralized}. You have selected ${inactiveSelectedSeatCount} inactive seats for reactivation. Remove seats from your selection or contact FlowJo sales to discuss adding more seats to the license.`,
        );
      } else if (openSeats === 0) {
        locErrorMessageArray.push(
          `Your license currently has ${openSeats} open ${seatsPluralized}. Contact FlowJo sales to discuss adding more seats to the license.`,
        );
      } else if (openSeats < 0) {
        // A reduced maxActiveUsers/maxSharedComputers can leave a license over its seat quota
        // (openSeats negative) rather than merely at 0 - a distinct state from "no seats open"
        // that deserves its own message rather than falling through with no warning at all.
        const overQuotaCount = Math.abs(openSeats);
        locErrorMessageArray.push(
          `Your license is currently ${overQuotaCount} ${Utils.pluralize(overQuotaCount, 'seat')} over its quota. Contact FlowJo sales to discuss adding more seats to the license.`,
        );
      }
    }

    // consider if the all the selected seats are already active
    // the admin is permitted to update the endDate for their active seats even if they have no open seats
    const selectedUserIds = [...selectedRows];
    const activeUserIds = this.selectedUsersActiveIds();
    if (selectedUserIds.length === activeUserIds.length) {
      this.updateSeats();
    } else if (!locErrorMessageArray.length) {
      this.updateSeats();
    } else {
      this.setState({ errorMessageArray: locErrorMessageArray });
    }
  }

  onModalKeyPress(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Enter' && (event.target as HTMLElement).className.includes('no-end-date')) {
      this.setState({
        seatDeactivationDate: null,
        selectedOption: 'No Deactivation Date',
        defaultDateString: 'No Deactivation Date',
        dateModificationRequired: true,
      });
    }
  }

  handleCloseModal = (): void => {
    this.dismissConfirmations();
    this.props.hideModal();
    this.props.clearRowsSelected();
  };

  /**
   * Acknowledges a confirmation and goes back to Manage Users, without closing it. Every OK
   * does this and nothing more: closing is Close's job alone, so a superadmin can apply one
   * section, read what happened, and carry on with the other.
   */
  dismissConfirmations = (): void => {
    this.props.closeProductManageUsersResults();
    this.props.closeSetUsersSuspendedResults();
    this.props.closeProductSetUsersArchivedDateResults();
    this.setState({ errorMessageArray: [], seatFeatureConfirmation: null });
  };

  /** Whether a seat-status Apply has a result waiting to be acknowledged. */
  hasSeatStatusResult(): boolean {
    const { suspendUsersResult, productSetUsersEndDate, productSetUsersArchivedDate } =
      this.props.userAdminState;
    return !!(
      (suspendUsersResult && suspendUsersResult !== 'closed') ||
      (productSetUsersEndDate && productSetUsersEndDate !== 'closed') ||
      (productSetUsersArchivedDate && productSetUsersArchivedDate !== 'closed')
    );
  }

  handleKeyPress = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      if (this.state.showArchiveConfirmation) {
        this.handleArchiveCancel();
      } else if (this.state.seatFeatureConfirmation?.length || this.hasSeatStatusResult()) {
        // Escape dismisses the topmost thing, which is a confirmation. Closing Manage Users out
        // from under it would skip past a result the superadmin has not read yet.
        this.dismissConfirmations();
      } else {
        this.handleCloseModal();
      }
    }
  };

  handleArchiveConfirm = (): void => {
    this.setState({ showArchiveConfirmation: false });
    this.updateSeatsArchivedDate();
  };

  handleArchiveCancel = (): void => {
    this.setState({ showArchiveConfirmation: false });
  };

  renderSelectedNumberOfUsers(number: number): React.ReactElement {
    return (
      <AlertMessage noIcon>
        {number} {Utils.pluralize(number, 'user')} selected. Changes are applied to all the selected
        seats.
      </AlertMessage>
    );
  }

  handleActiveButton = (): void => {
    // Check if current user is an admin
    if (!this.checkAdminAndSetError({ selectedOption: ADD })) {
      return;
    }

    // When activating, clear past deactivation dates
    const shouldClearDate =
      !this.state.seatDeactivationDate || this.state.seatDeactivationDate <= new Date();

    // The conditional spread makes `seatDeactivationDate`/`dateModificationRequired`
    // optional keys, which React's `setState<K>` (expects required `Pick<State, K>`)
    // rejects. The runtime object is a valid partial state update; assert the shape.
    this.setState({
      selectedOption: ADD,
      errorMessageArray: [],
      ...(shouldClearDate && {
        seatDeactivationDate: null,
        dateModificationRequired: true,
      }),
    } as Pick<
      State,
      'selectedOption' | 'errorMessageArray' | 'seatDeactivationDate' | 'dateModificationRequired'
    >);
  };

  handleInactiveButton = (): void => {
    const currentSeatDeactivationDate = this.state.seatDeactivationDate;
    const now = new Date();
    const updateDate =
      (currentSeatDeactivationDate as Date) >= now ? now : currentSeatDeactivationDate;

    // Check if current user is an admin
    if (!this.checkAdminAndSetError({ selectedOption: REMOVE, seatDeactivationDate: updateDate })) {
      return;
    }
    this.setState({
      selectedOption: REMOVE,
      errorMessageArray: [],
      seatDeactivationDate: updateDate,
    });
    const { currentProductAccess: productAccess, selectedRows } = this.props;
    const rowsSelectedIdsArray = [...selectedRows];
    // check if any of the selected seats are already inactive
    let count = 0;
    let errorMessages: string[] = [];
    rowsSelectedIdsArray.forEach((id) => {
      const currentUser = productAccess.users[id];
      if (
        currentUser &&
        !AuthUtils.isUserSeatActive(
          currentUser.suspended,
          currentUser.startDate,
          currentUser.endDate,
        )
      ) {
        count += 1;
      }
    });
    if (count > 0) {
      errorMessages.push('One or more of your selected user seats are already deactivated.');
      errorMessages = errorMessages.concat(this.state.errorMessageArray);
    }
    if (
      !this.state.seatDeactivationDate ||
      new Date(this.state.seatDeactivationDate) < new Date()
    ) {
      this.setState({
        seatDeactivationDate: new Date(),
        dateModificationRequired: true,
        errorMessageArray: errorMessages,
      });
    }
  };

  handleArchiveButton = (): void => {
    // Check if current user is an admin
    if (
      !this.checkAdminAndSetError({
        selectedOption: ARCHIVE,
        seatDeactivationDate: new Date(),
        dateModificationRequired: true,
      })
    ) {
      return;
    }

    this.setState({
      selectedOption: ARCHIVE,
      errorMessageArray: [],
      seatDeactivationDate: new Date(),
      dateModificationRequired: true,
    });
  };

  handleDatePicker = (date: Date | null): void => {
    const today = new Date();
    if (!date || date > today) {
      this.handleActiveButton();
      this.setState({
        seatDeactivationDate: date,
        defaultDateString: 'No Deactivation Date',
        dateModificationRequired: true,
      });
    } else if (date <= today) {
      // the Admin is deactivating the seats
      this.handleInactiveButton();
      this.setState({
        seatDeactivationDate: today,
        dateModificationRequired: true,
      });
    }
  };

  renderArchiveConfirmationModal(): React.ReactElement {
    const { selectedRows } = this.props;
    const userCount = selectedRows.length;

    return (
      <Modal
        size={fjModalSizes.SM}
        onRequestClose={this.handleArchiveCancel}
        cancel={{
          text: 'Cancel',
          callback: this.handleArchiveCancel,
        }}
        accept={{
          text: 'Confirm',
          callback: this.handleArchiveConfirm,
        }}
        title="Confirm Archive"
        dataTestId="archive-confirmation-modal"
      >
        <div>
          <p>
            You are about to archive {userCount} {Utils.pluralize(userCount, 'user')}.
          </p>
          <p>Archiving users will:</p>
          <ul>
            <li>Immediately deactivate their licenses</li>
            <li>Hide them from the default user list</li>
            <li>Preserve their data for record-keeping</li>
          </ul>
          <p>
            <strong>This action can be reversed by reactivating the users.</strong>
          </p>
          <p>Click Confirm to proceed.</p>
        </div>
      </Modal>
    );
  }

  renderSeatUsageControls(_selectedProduct?: unknown): React.ReactElement {
    return (
      <StatusContainer>
        <span>Change Seat Status</span>
        <div>
          <RadioButton
            label="Active"
            checked={this.state.selectedOption === ADD}
            onChange={this.handleActiveButton}
          />
          <RadioButton
            label="Inactive"
            checked={this.state.selectedOption === REMOVE}
            onChange={this.handleInactiveButton}
          />
          <RadioButton
            label="Archived"
            checked={this.state.selectedOption === ARCHIVE}
            onChange={this.handleArchiveButton}
          />
        </div>
        <div style={{ paddingTop: '20px', paddingBottom: '6px', fontWeight: 400 }}>
          Deactivation Date
        </div>
        <DatePicker
          selected={this.state.seatDeactivationDate}
          dataTestId="deactivation"
          onChange={this.handleDatePicker}
          time={DatePicker.END_OF_DAY}
          disabled={this.state.selectedOption === ARCHIVE}
          // `placeholderText`/`popperPlacement`/`fixedHeight`/`dateFormat` are
          // forwarded by DatePicker to react-datepicker via its rest spread but
          // are absent from the typed DatePickerProps; spread them so the
          // assignment is structural-checked (mirrors the codebase pattern).
          {...{
            placeholderText: this.state.defaultDateString,
            popperPlacement: 'right-end',
            fixedHeight: true,
            dateFormat: 'yyyy-MMM-dd',
          }}
        />
        <div
          className="no-end-date fjg-col_8of64 padding-left-sm padding-top-sm height-30px border-box"
          role="button"
          tabIndex={0}
          data-testid="no-deactivation-date-button"
          style={{
            opacity: this.state.selectedOption === ARCHIVE ? 0.5 : 1,
            pointerEvents: this.state.selectedOption === ARCHIVE ? 'none' : 'auto',
          }}
          onKeyPress={(event) => {
            this.onModalKeyPress(event);
          }}
          onClick={() => {
            this.setState({
              seatDeactivationDate: null,
              defaultDateString: 'No Deactivation Date',
              dateModificationRequired: true,
            });
            this.handleActiveButton();
          }}
        >
          <RadioButton
            checked={this.state.seatDeactivationDate === null}
            label="No Deactivation Date"
            data-testid="radio-no-deactivation-date"
            disabled={this.state.selectedOption === ARCHIVE}
          />
        </div>
      </StatusContainer>
    );
  }

  /**
   * Per-seat feature overrides, superadmin only.
   *
   * Gated on the sudo JWT claim rather than utils/isSuperAdmin(): this component renders in the
   * normal build too, where that flag is a compile-time false for everyone. A UI affordance only
   * - the backend rejects a non-superadmin regardless.
   */
  renderFeatureControls(): React.ReactElement | null {
    if (!currentUserHasSudoClaim()) {
      return null;
    }

    const { currentProductAccess, selectedRows, seatFeatures } = this.props;
    const licenseFeatures = (currentProductAccess?.features || {}) as Record<
      string,
      LicenseFeature
    >;
    const userIds = [...selectedRows];
    const rows = (seatFeatures || []) as SeatFeatureRow[];

    // The shared component renders a catalog-shaped list, so present the license's own
    // features in that shape. Only features the license actually has are offered, since a
    // seat cannot be granted something the license lacks.
    const availableFeatures = Object.values(licenseFeatures)
      // A feature the license does not grant is not offered at the seat level at all - not
      // even as a disabled control - since a seat can never widen past the license.
      .filter((feature) => !!feature.name && isLicenseEnabled(licenseFeatures, feature.name))
      .map((feature) => ({
        id: feature.productFeatureId || (feature.name as string),
        // eslint-disable-next-line camelcase
        feature_name: feature.name as string,
        // eslint-disable-next-line camelcase
        value_type: feature.valueType || 'boolean',
      }))
      .sort((a, b) => a.feature_name.localeCompare(b.feature_name));

    // Nothing to offer means no section at all, rather than an empty-state message. The
    // license modal still shows its empty state; there it tells a superadmin the section exists.
    if (!availableFeatures.length) {
      return null;
    }

    const stateFor = (featureName: string): 'on' | 'off' => {
      const licenceOn = isLicenseEnabled(licenseFeatures, featureName);
      const pending = this.state.seatFeaturePending[featureName];
      if (pending !== undefined) {
        return pending ? 'on' : 'off';
      }
      return selectionState(licenceOn, rows, userIds, featureName);
    };

    return (
      <FeatureSection data-testid="seat-feature-controls">
        <SectionHeading data-testid="section-heading-features">Features (superadmin)</SectionHeading>
        <ProductFeaturesManager
          title="Product Features"
          defaultExpanded
          footer={this.renderActionRow({
            testId: 'seat-feature-actions',
            // Nothing staged means nothing to save.
            applyDisabled: !Object.keys(this.state.seatFeaturePending).length,
            onApply: () => {
              const outcome = this.applySeatFeatureChanges();
              // Swaps this modal for the confirmation, the same way a seat-status Apply does.
              this.setState({ seatFeaturePending: {}, seatFeatureConfirmation: outcome });
            },
          })}
          availableFeatures={availableFeatures}
          // Seat overrides are applied through setSeatFeature, not through the license-level
          // onChange this component uses for its other consumer.
          onChange={() => undefined}
          fetchFeatures={() => undefined}
          resolveState={stateFor}
          onToggleFeature={(featureName) =>
            this.setState((prev) => ({
              seatFeaturePending: {
                ...prev.seatFeaturePending,
                [featureName]: nextStateOnToggle(stateFor(featureName)),
              },
            }))
          }
        />
      </FeatureSection>
    );
  }

  /** Applies every staged feature change to all selected seats. */
  /**
   * Applies every staged feature change to all selected seats, and reports what each one did
   * to the seats as they were beforehand - which is what the confirmation shows, and which
   * cannot be recovered afterwards because the writes have landed by then.
   */
  applySeatFeatureChanges(): SeatFeatureOutcome[] {
    const { currentProductAccess, userAdminState, selectedRows, seatFeatures, setSeatFeature } =
      this.props;
    const costCenterId = userAdminState?.costCenter?.id;
    const pending = this.state.seatFeaturePending;

    if (!setSeatFeature || !costCenterId || !currentProductAccess?.id) {
      return [];
    }

    const licenseFeatures = (currentProductAccess?.features || {}) as Record<
      string,
      LicenseFeature
    >;
    const rows = (seatFeatures || []) as SeatFeatureRow[];
    const userIds = [...selectedRows];

    return Object.keys(pending).map((featureName) => {
      const enabled = pending[featureName];
      const before = selectionSummary(
        isLicenseEnabled(licenseFeatures, featureName),
        rows,
        userIds,
        featureName,
      );

      setSeatFeature({
        costCenterId,
        productAccessId: currentProductAccess.id,
        userIds,
        featureName,
        // Booleans are the only value type these controls produce; the value mirrors enabled.
        value: enabled,
        enabled,
      });

      return {
        featureName,
        enabled,
        total: before.total,
        wasEnabled: before.enabled,
        wasDisabled: before.disabled,
        wasMixed: before.mixed,
      };
    });
  }

  /**
   * The Apply for one section. Each section saves independently of the other, so each owns its
   * own button; discarding is not per-section - the modal's single Close shuts it and drops
   * everything staged in both.
   */
  renderActionRow({
    testId,
    applyDisabled,
    onApply,
  }: {
    testId: string;
    applyDisabled: boolean;
    onApply: () => void;
  }): React.ReactElement {
    return (
      <ActionRow data-testid={testId}>
        <Button onClick={onApply} disabled={applyDisabled} data-testid={`${testId}-apply`}>
          Apply
        </Button>
      </ActionRow>
    );
  }

  renderModalBeforeManage({
    selectedProduct,
    selectedRows,
    errorMessageArray,
  }: {
    selectedProduct: unknown;
    selectedRows: number;
    errorMessageArray: string[];
  }): React.ReactElement {
    return (
      <Modal
        size={fjModalSizes.LG}
        onRequestClose={this.handleCloseModal}
        title="Manage Users"
        showX
        onClickX={this.handleCloseModal}
        dataTestId="manage-users-modal"
      >
        <SectionHeading data-testid="section-heading-seat-status">Seat Status</SectionHeading>
        <SectionBox data-testid="seat-status-section">
          <span>Deactivate or reactivate user seats on your license.</span>
          {this.renderSelectedNumberOfUsers(selectedRows)}
          {this.renderSeatUsageControls(selectedProduct)}
          <SectionBoxFooter>
            {this.renderActionRow({
              testId: 'seat-status-actions',
              applyDisabled: !this.enableApply(),
              onApply: () => this.handleChange(),
            })}
          </SectionBoxFooter>
        </SectionBox>
        {this.renderFeatureControls()}
        <AlertMessage>{errorMessageArray && errorMessageArray[0]}</AlertMessage>
        {/*
          Below every section and inside none of them. Each Apply saves its own box; this saves
          nothing and shuts the modal, so it is named for that rather than for cancelling
          something. Rendered here rather than through FjModal's `cancel` prop because that
          slot is a `fjButtonTypes.LINK` shared by every modal in the app - it reads as a link,
          not a button, and it is not this component's to restyle.
        */}
        <ModalActionRow data-testid="modal-actions">
          <Button onClick={this.handleCloseModal} data-testid="modal-close">
            Close
          </Button>
        </ModalActionRow>
      </Modal>
    );
  }

  renderResponseModal = ({ selectedRowsAll }: { selectedRowsAll: unknown }): React.ReactElement => {
    let rowsSelected: unknown[] = [];
    if (this.locRowsSelected.length === 0) {
      rowsSelected = selectedRowsAll as unknown[];
    } else {
      rowsSelected = this.locRowsSelected.slice();
    }

    return (
      <ResponseModal
        updatedUsers={rowsSelected}
        error=""
        uiArrayFailed={[]}
        // Same as the feature confirmation: acknowledge and go back, do not close.
        closeModal={() => this.dismissConfirmations()}
      />
    );
  };

  /**
   * Reports what the feature Apply just did, in place of Manage Users. Carries the explanation
   * the feature rows used to show inline: it belongs to the moment the change is made, not to
   * a row that has to keep restating it.
   */
  renderSeatFeatureConfirmationModal(outcome: SeatFeatureOutcome[]): React.ReactElement {
    const names = outcome.map(({ featureName }) => featureName);
    return (
      <Modal
        // OK acknowledges and returns to Manage Users; only Close closes.
        accept={{ text: 'OK', callback: this.dismissConfirmations }}
        title={`${names.length === 1 ? names[0] : 'Features'} updated`}
        size={fjModalSizes.SM}
        dataTestId="seat-feature-confirmation"
      >
        {outcome.map(({ featureName, enabled, total, wasEnabled, wasDisabled, wasMixed }) => {
          const seats = Utils.pluralize(total, 'seat');
          return (
            <div key={featureName} data-testid={`feature-outcome-${featureName}`}>
              {/*
                What happened leads, since that is the answer; the split it flattened follows as
                an aside. `strong` rather than a styled span: the state is the one word a reader
                scans for, and a plain tag adds no class to every snapshot below it.
              */}
              <div data-testid={`feature-outcome-result-${featureName}`}>
                {`All ${total} selected ${seats} ${total === 1 ? 'was' : 'were'} set to `}
                <strong data-testid={`feature-outcome-state-${featureName}`}>
                  {enabled ? 'enabled' : 'disabled'}
                </strong>
                .
              </div>
              {wasMixed && (
                // Red for the same reason it was red in the row: the selection disagreed, and
                // Apply has just flattened it. Parenthesised because it qualifies the line
                // above rather than standing on its own.
                <MixedNote data-testid={`feature-outcome-mixed-${featureName}`}>
                  {`(${wasEnabled} of ${total} selected ${seats} had this enabled, ${wasDisabled} disabled.)`}
                </MixedNote>
              )}
            </div>
          );
        })}
      </Modal>
    );
  }

  render(): React.ReactNode {
    const {
      userAdminState: { suspendUsersResult },
      selectedProduct,
      selectedRows,
      selectedRowsAll,
      userAdminState: { productSetUsersEndDate, productSetUsersArchivedDate },
    } = this.props;

    // Show archive confirmation modal
    if (this.state.showArchiveConfirmation) {
      return this.renderArchiveConfirmationModal();
    }

    const showSeatStatusResult =
      (suspendUsersResult && suspendUsersResult !== 'closed') ||
      (productSetUsersEndDate && productSetUsersEndDate !== 'closed') ||
      (productSetUsersArchivedDate && productSetUsersArchivedDate !== 'closed');

    // Both confirmations sit ON TOP of Manage Users rather than replacing it: their OK only
    // acknowledges, so the modal it returns to should never have gone away. They are
    // react-modal portals sharing an overlay z-index, so the later one in the tree paints on
    // top - the order here is load-bearing, and e2e/seat-features.spec.ts hit-tests it.
    return (
      <>
        {this.renderModalBeforeManage({
          selectedProduct,
          selectedRows: selectedRows.length,
          errorMessageArray: this.state.errorMessageArray,
        })}
        {showSeatStatusResult ? this.renderResponseModal({ selectedRowsAll }) : null}
        {this.state.seatFeatureConfirmation?.length
          ? this.renderSeatFeatureConfirmationModal(this.state.seatFeatureConfirmation)
          : null}
      </>
    );
  }
}

export default ManageUsersModal;
